import { useDispatch } from 'react-redux'
import axios from 'axios'
import { REST_SERVICE_URL_ROOT, CLIENT_ID, CLIENT_SECRET, IDS_TOKEN_ENDPOINT, IDS_TOKEN_COOKIE, HRIS_TOKEN_COOKIE } from '../config/settings'
import configureStore, { getStore } from '@AppRedux/store'
import qs from 'qs'
import { saveAs } from 'file-saver'
import { FILE_TYPES } from '@Utils/constants'
import * as LoginActions from '@Containers/Login/state/actions'
import cookie from 'react-cookies';

class ApiService {
    constructor() {
        axios.defaults.baseURL = REST_SERVICE_URL_ROOT

        let service = axios.create()
        // Request Interceptor
        service.interceptors.request.use((config) => {
            try {

                // set expiration of token to 1 day
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);

                let cookieValue = document.cookie.replace(/(?:(?:^|.*;\s*)webhrisids_authtoken\s*\=\s*([^;]*).*$)|^.*$/, "$1");

                cookie.save(IDS_TOKEN_COOKIE, cookieValue, {
                    expires: tomorrow,
                    path: '/'
                })

                let token = cookie.load(IDS_TOKEN_COOKIE, { path: '/' });
                let hrisToken = cookie.load(HRIS_TOKEN_COOKIE, { path: '/' });
                // Change depending on your authorization token variable
                config.headers.common['Authorization'] = 'Bearer ' + token
                config.headers.common['Content-Type'] = 'application/json'
                config.headers.common['HRIS-Authorization'] = 'Bearer ' + hrisToken
            } catch {
                console.log('Unauthorized')
            }

            //  Do something before request is sent
            return config
        }, (error) => {
            //  Do something with request error
            return this.handleError(error);
        })

        // Response Interceptor
        service.interceptors.response.use((response) => this.handleSuccess(response), (error) => this.handleError(error))

        this.service = service
    }

    handleSuccess(response) {
        return response
    }

    // Update actions for error handling
    async handleError(error) {
        const status = error.response ? error.response.status : null
        const originalConfig = error.config
        let errorData = {
            title: 'Network Error',
            message: 'There was an error processing your request.'
        }

        switch (status) {
            case 401:
                if (originalConfig.url !== '/token' && !originalConfig._retry) {
                    originalConfig._retry = true
                    try {
                        const rs = await this.refresh()
                        return this.service(originalConfig)
                    } catch (_error) {
                        window.location.reload();
                        return Promise.reject(_error)
                    }
                }
            case 404:
                errorData.title = 'Page Not Found'
                errorData.message = 'Page not found!'
                getStore().dispatch({ type: 'SHOW_XHR_ERROR', payload: errorData })
                break
            case 500:
                errorData.title = 'Network Error'
                errorData.message = 'There was an error processing your request. Please contact administrator if issue persists.'
                getStore().dispatch({ type: 'SHOW_XHR_ERROR', payload: errorData })
                break
            default:
                if (error.request && error.message.includes('Network Error')) {
                    errorData.title = 'Network Error'
                    errorData.message = 'The network connection is lost. Please contact administrator if issue persists.'
                }
                getStore().dispatch({ type: 'SHOW_XHR_ERROR', payload: errorData })
                break
        }

        return Promise.reject(error)
    }

    redirectTo = (document, path) => {
        document.location = path
    }

    get(path) {
        return this.service.get(path)
    }

    get(path, payload) {
        return this.service.get(path, {
            params: payload,
            paramsSerializer: params => {
                return qs.stringify(params, { allowDots: true })
            }
        })
    }

    token(payload) {
        let params = new URLSearchParams()
        params.append('username', payload.username)
        params.append('password', payload.password)
        params.append('client_id', CLIENT_ID) // Added: 2022/01/27
        params.append('client_secret', CLIENT_SECRET)
        params.append('scope', 'scope2')
        params.append('grant_type', 'password')

        return this.service.request({
            method: 'POST',
            url: IDS_TOKEN_ENDPOINT,
            responseType: 'json',
            data: params,
            headers: {
                'Authorization': 'Basic ' + btoa(CLIENT_ID + ':' + CLIENT_SECRET),
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        })
    }

    async refresh() {
        const authentication = configureStore().getState().auth;
        if (!authentication) {
            // check if user credentials still exist,
            // do not refresh if no data
            return Promise.reject('No user data detected.')
        }
        let credentials = authentication.credentials
        let params = new URLSearchParams()
        params.append('username', credentials.userId)
        params.append('client_id', CLIENT_ID)
        params.append('refresh_token', credentials.refreshToken)
        params.append('grant_type', 'password')

        return await this.service.request({
            method: 'POST',
            url: REST_SERVICE_URL_ROOT + '/refresh',
            responseType: 'json',
            data: params,
            headers: {
                'Authorization': 'Basic ' + btoa(CLIENT_ID + ':' + CLIENT_SECRET),
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }).then((response) => {
            if (response.status === 200) {
                const { data } = response;
                let loginData = {
                    authToken: data.AccessToken,
                    refreshToken: data.RefreshToken,
                    userFullName: data.UserFullName,
                    position: data.Position,
                    userLogEmployee: data.UsageLogEmployee,
                    companyId: parseInt(data.CompanyID, 10),
                    accountId: parseInt(data.AccountID, 10),
                    userId: parseInt(data.ID, 10),
                    userLogUserName: data.UsageLogUserName,
                    systemRole: parseInt(data.SystemRole, 10),
                    name: data.Username,
                    employeeId: data.EmployeeID,
                    moduleAccessList: data.Module,
                    accessLevelId: parseInt(data.AccessLevelID, 10),
                    isAdmin: data.IsAdmin,
                    isApprover: "False",
                    isTimeKeeping: data.isTimeKeeping,
                    isPaytollReg: data.IsPaytollReg,
                    accessList: '',
                }
                getStore().dispatch(LoginActions.setUserAuth(loginData, true));
            }
            return response
        })
    }

    patch(path, payload) {
        return this.service.request({
            method: 'PATCH',
            url: path,
            responseType: 'json',
            data: payload
        }).then((response) => response)
    }

    post(path, payload) {
        return this.service.request({
            method: 'POST',
            url: path,
            responseType: 'json',
            data: payload
        }).then((response) => response)
    }

    put(path, payload) {
        return this.service.request({
            method: 'PUT',
            url: path,
            responseType: 'json',
            data: payload
        }).then((response) => response)
    }

    delete(path, payload) {
        return this.service.request({
            method: 'DELETE',
            url: path,
            responseType: 'json',
            data: payload
        }).then((response) => response).catch(error => error)
    }

    export(path, filename, payload, filetype = FILE_TYPES.XLSX) {
        return this.service.get(path, {
            params: payload,
            paramsSerializer: params => {
                return qs.stringify(params, { allowDots: true })
            },
            responseType: 'blob',
        }).then((response) => {
            const disposition = response.headers['content-disposition']
            let useFilename = filename
            if (disposition) {
                const filenameRegex = /filename=(?<name>.+?)([;\s]).*/
                const match = filenameRegex.exec(disposition);
                if (match && useFilename === '') {
                    useFilename = match.groups.name;
                }
            }
            saveAs(new Blob([response.data]), useFilename)
        }).catch(error => error)
            .finally(() => {
                return true
            })
    }

    print(path, filename, payload) {
        return this.service.get(path, {
            params: payload,
            paramsSerializer: params => {
                return qs.stringify(params, { allowDots: true })
            },
            responseType: 'blob',
        }).then((response) => {
            const blob = new Blob([response.data], { type: 'application/pdf' })
            const blobURL = URL.createObjectURL(blob)

            let existFrame = document.getElementById('print_frame')
            if (existFrame) existFrame.remove()

            const iframe = document.createElement('iframe')
            iframe.setAttribute('id', 'print_frame')
            document.body.appendChild(iframe)

            iframe.style.display = 'none'
            iframe.src = blobURL
            iframe.onload = function () {
                setTimeout(function () {
                    iframe.focus()
                    iframe.contentWindow.print()
                }, 1)
            }
        }).catch((error) => error)
            .finally(() => {
                return true
            })
    }
}

export default new ApiService()
